<template>
  <div class="sticky__table">
    <header class="header" :class="{ header_noactions: !hasTopActions }">
      <div class="text_controls">
        <span class="title">{{ title }}</span>
        <slot name="top-actions"></slot>
      </div>
      <div class="top_controls">
        <div
          class="searchbar_container"
          :class="{ searchbar_container_full: hasTopActions }"
        >
          <SearchBar
            class="searchbar"
            :placeholder="searchbarPlaceholder"
            v-if="searchable || searching"
            v-on:input="
              $emit('search', {
                term: $event,
                columns: searchableColumns,
              })
            "
          />
        </div>
        <slot name="filter"></slot>
      </div>
    </header>
    <hr class="divider" />
    <div class="table-container">
      <table v-if="!loading" v-bind="$attrs" class="table">
        <thead>
          <tr>
            <th class="checkbox-cell">
              <Checkbox
                class="table_header__cell"
                :value="selectHeaderCheckbox"
                @click="handleSelect"
                :partiallySelected="partiallySelected"
                :filteredItemsSelected="filteredItemsSelected"
              />
            </th>
            <th v-for="(col, index) in columns" v-bind:key="index">
              <div
                class="table_header__cell"
                :class="{
                  'table_header__cell--sorted': isSorted(col),
                  'table_header__cell--nowrap': disableHeaderTextWrap,
                }"
              >
                <span
                  :class="{ 'table_header__text--sortable': isSortable(col) }"
                  v-on:click="
                    isSortable(col) &&
                      $emit('sort', getSortColumnAndDirection(col))
                  "
                >
                  {{ col.label }}
                </span>
                <span v-if="col.isRequired" class="required_column"> * </span>
                <div v-if="isSortable(col)" class="table_header__sort_controls">
                  <button
                    class="table_header__sort_button"
                    :class="{
                      'table_header__sort_button--sorted':
                        isSorted(col) &&
                        currentSortDirection === SORT_DIRECTION_DESCENDING,
                    }"
                    v-on:click="
                      $emit('sort', {
                        column: col.property,
                        direction: SORT_DIRECTION_DESCENDING,
                      })
                    "
                  >
                    <FontAwesomeIcon
                      :icon="['fas', 'caret-up']"
                      fixed-width
                      transform="down-2"
                    />
                  </button>
                  <button
                    class="table_header__sort_button"
                    :class="{
                      'table_header__sort_button--sorted':
                        isSorted(col) &&
                        currentSortDirection === SORT_DIRECTION_ASCENDING,
                    }"
                    v-on:click="
                      $emit('sort', {
                        column: col.property,
                        direction: SORT_DIRECTION_ASCENDING,
                      })
                    "
                  >
                    <font-awesome-icon
                      :icon="['fas', 'caret-down']"
                      fixed-width
                      transform="up-2"
                    />
                  </button>
                </div>
              </div>
            </th>
            <th class="button-cell">
              <div class="table_header__cell edit-cell" @click="editHeader">
                <IconButton
                  v-tippy="{ content: editHeaderTooltip }"
                  label=""
                  class="edit_button"
                >
                  <font-awesome-icon :icon="['fas', 'edit']" />
                </IconButton>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="table_body">
          <slot></slot>
        </tbody>
      </table>
    </div>
    <div v-if="loading" class="loading">
      <LoaderCircular />
    </div>
    <div v-if="empty && !loading" class="empty">
      <slot name="empty"></slot>
    </div>
    <div class="actions">
      <slot name="actions"> </slot>
    </div>
    <hr v-if="paginate" class="actions_divider" />
    <div class="footer" v-if="paginate">
      <RowsPerPage
        :text="rowsPerPageText"
        :defaultPaginationSize="defaultPaginationSize"
        :paginationSizeOptions="paginationSizeOptions"
        @onPaginationSizeChange="$emit('onPaginationSizeChange', $event)"
      />
      <div class="pagination_indicator">
        <slot name="pagination-indicator">
          <span>{{ `Page ${currentPage} of ${pages}` }}</span>
        </slot>
      </div>
      <div v-if="pages > 1" class="footer__buttons">
        <Pagination
          :pageIndex="currentPage"
          :pageCount="pages"
          @pageChange="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  faCaretDown,
  faCaretUp,
  faEdit,
} from "@fortawesome/pro-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import IconButton from "../atoms/IconButton.vue";
import Checkbox from "../atoms/Inputs/Checkbox.vue";
import Pagination from "./Pagination.vue";
import SearchBar from "../atoms/Inputs/SearchBar.vue";
import {
  SORT_DIRECTION_ASCENDING,
  SORT_DIRECTION_DESCENDING,
  NO_SORT,
} from "../../composables/useTableState";
import LoaderCircular from "../atoms/LoaderCircular.vue";
import RowsPerPage from "../atoms/RowsPerPage.vue";

library.add(faCaretUp, faCaretDown, faEdit);

export default {
  name: "StickyTable",
  components: {
    FontAwesomeIcon,
    SearchBar,
    Pagination,
    IconButton,
    Checkbox,
    LoaderCircular,
    RowsPerPage,
  },
  props: {
    /**
     * @vuese
     * Title will be on the top of the Card.
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Structure:
     * [{ label: 'Name', property: 'name'}]
     * Where the property column should match the property yielding the value for the column.
     */
    columns: {
      type: Array,
      required: true,
    },
    /**
     * Flag to indicate whether the table should render. When true a placeholder text is displayed.
     */
    empty: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag to render sorting buttons and emit corresponding events.
     */
    sortable: {
      type: Boolean,
      default: false,
    },
    /**
     * When sortable is true you need to specify which columns are actually sortable.
     * eg ['name', 'id']
     */
    sortableColumns: {
      type: Array,
    },
    /**
     * When sorting is active you should tell this component what col is currently sorted to apply proper styling.
     */
    currentSorted: {
      type: String,
    },
    /**
     * When sorting is active you should tell which direction is being sorted.
     */
    currentSortDirection: {
      type: String,
    },
    /**
     * When true, a filter like search input is displayed in the header part if the card.
     */
    searchable: {
      type: Boolean,
      default: false,
    },
    /**
     * To indicate that the current data present in table is the product of a filter/search result.
     */
    searching: {
      type: Boolean,
      default: false,
    },
    /**
     * You need to specify what columns are included during search.
     */
    searchableColumns: {
      type: Array,
    },
    /**
     * Placeholder text to be displayed in the searchbar if present
     */
    searchbarPlaceholder: {
      type: String,
      default: "Search",
    },
    /**
     * Flag to indicate the data in the table is paginated. Will render page controls in footer and emit page navigation events.
     */
    paginate: {
      type: Boolean,
      default: true,
    },
    /**
     * Current page
     */
    currentPage: {
      type: Number,
    },
    /**
     * Total amount of pages.
     */
    pages: {
      type: Number,
    },
    /**
     * Setting this to true will prevent the wrapping of the column text
     */
    disableHeaderTextWrap: {
      type: Boolean,
      default: false,
    },
    /**
     * Setting this to true will select header checkbox, and all checkboxes in table
     */
    selectHeaderCheckbox: {
      type: Boolean,
      default: false,
    },
    /**
     * Tooltip text to be displayed on 'Edit header' hover
     */
    editHeaderTooltip: {
      type: String,
      default: "Change fields",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    partiallySelected: {
      type: Boolean,
    },
    filteredItemsSelected: {
      type: Boolean,
    },
    rowsPerPageText: {
      type: String,
    },
    defaultPaginationSize: {
      type: Number,
    },
    paginationSizeOptions: {
      type: Array,
    },
  },
  computed: {
    hasTopActions() {
      return !!this.$scopedSlots["top-actions"];
    },
  },
  methods: {
    isSortable(col) {
      return this.isSortable
        ? this.sortableColumns.indexOf(col.property) !== -1
        : false;
    },
    isSorted(col) {
      return this.isSortable ? col.property === this.currentSorted : false;
    },
    getSortColumnAndDirection(col) {
      switch (this.currentSortDirection) {
        case NO_SORT:
          return {
            column: col.property,
            direction: SORT_DIRECTION_ASCENDING,
          };
        case SORT_DIRECTION_ASCENDING:
          return {
            column: col.property,
            direction: SORT_DIRECTION_DESCENDING,
          };
        default:
          return {
            column: null,
            direction: NO_SORT,
          };
      }
    },
    onPageChange(page) {
      this.$emit("pageChange", page);
    },
    editHeader() {
      this.$emit("editHeader");
    },
    handleSelect(event) {
      if (this.partiallySelected && this.filteredItemsSelected) {
        // eslint-disable-next-line no-param-reassign
        event = false;
      }
      this.$emit("selectAll", event);
    },
  },
  created() {
    this.SORT_DIRECTION_ASCENDING = SORT_DIRECTION_ASCENDING;
    this.SORT_DIRECTION_DESCENDING = SORT_DIRECTION_DESCENDING;
  },
};
</script>

<style lang="scss" scoped>
@import "~include-media";
@import "../../style_variables/colors";
@import "../../style_variables/typography_classes";
@import "../../style_variables/breakpoints";
$open_sans: "Open Sans", sans-serif;

// --- Start Reset --- //
table,
caption,
tbody,
tfoot,
thead,
tr,
th,
td {
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: middle;
}
table {
  border-collapse: collapse;
  border-spacing: 0;
}

td {
  padding: 30px 20px 8px 20px;
}

th {
  padding: 30px 20px 10px 20px;
}
// --- End Reset --- //

.header {
  padding: 12px 24px 20px 24px;
  &_noactions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0;
  }

  @include media("<tablet") {
    padding: 0 16px 20px 16px;
  }

  .text_controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .title {
    display: inline-block;
    @extend %headline6_style;
    font-family: "Montserrat", sans-serif;
    font-size: 1.25rem;
    font-weight: 700;
    white-space: nowrap;
    margin: 0;

    @include media("<tablet") {
      display: none;
    }
  }
  .top_controls {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    align-items: flex-end;

    .searchbar_container {
      width: 320px;
      @include media("<tablet") {
        width: 100%;
      }
      &_full {
        width: 100%;
        margin-top: 24px;
      }
    }

    .searchbar {
      width: 100%;
    }
  }
}

.divider {
  border: none;
  border-top: 1px solid #f2f2f7;
  margin: 0 24px;

  @include media("<tablet") {
    margin: 0 16px;
  }
}

.sticky__table {
  max-width: 950px;
  padding: 28px 0 24px 0;
  background-color: $white;
  border-radius: 8px;
  border: 1px solid $grey_alabaster;

  @include media($isPhone...) {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
}

.table {
  width: max-content;
  min-width: 100%;
  font-family: $open_sans;
  font-size: 0.75rem;
  margin-bottom: 12px;
  background: white;
  border-radius: 8px;
}

.table_header__cell {
  color: $grey_manatee;
  display: flex;
  align-items: center;
  cursor: default;
  user-select: none;
  font-weight: 600;

  &.table_header__cell--nowrap {
    white-space: nowrap;
  }

  .table_header__text--sortable {
    cursor: pointer;
  }

  .table_header__sort_controls {
    display: flex;
    flex-direction: column;
    margin-left: 4px;

    .table_header__sort_button {
      background: transparent;
      border: none;
      padding: 0;
      height: 12px;
      color: $grey_manatee;
      cursor: pointer;

      &.table_header__sort_button--sorted {
        color: $black;
      }
    }
  }

  &.table_header__cell--sorted {
    color: $black;
  }
  &.edit-cell {
    justify-content: flex-end;
  }
}

.footer {
  font-family: $open_sans;
  font-size: 0.875rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px 0 24px;
  @include media("<tablet") {
    padding: 0 16px;
  }
}

.footer__buttons {
  display: flex;
}

.empty {
  font-family: $open_sans;
  font-size: 0.75rem;
  color: $grey_bombay;
  font-weight: 400;
  padding-top: 16px;
  padding-left: 68px;
}

.loading {
  padding-top: 16px;
  display: flex;
  justify-content: center;
}

.table-container {
  background: white;
  border-radius: 8px;
  max-width: 950px;
  overflow-x: auto;
  &::-webkit-scrollbar {
    height: 10px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
  }
}

.checkbox-cell {
  position: sticky;
  z-index: 2;
  left: 0;
  background-color: white;
  padding-left: 24px;
  padding-right: 4px;
  width: 68px;
}

.button-cell {
  position: sticky;
  z-index: 2;
  right: 0;
  background-color: white;
  width: 68px;
  padding-right: 24px;
}

.fa-edit {
  color: #4389f8;
  font-size: 16px;
  &:hover {
    color: $blue_sapphire;
  }
}

.edit_button {
  display: inherit;
}

::-webkit-scrollbar {
  height: 6px;
  background-color: $white;
}
::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background-color: #c9c9c9;
}
::-webkit-scrollbar-track {
  margin-left: 64px;
  margin-right: 64px;
}

tbody > tr > :nth-child(2) {
  padding-left: 0px;
}

tbody > tr > :nth-last-child(2) {
  padding-right: 0px;
}
thead > tr > :nth-child(2) {
  padding-left: 0px;
}

thead > tr > :nth-last-child(2) {
  padding-right: 0px;
}

.required_column {
  color: $red;
}

.actions_divider {
  border: none;
  border-top: 1px solid $grey_alabaster;
  margin: 0px 24px;
  margin-bottom: 12px;
}

.pagination_indicator {
  margin-left: auto;
  margin-right: 20px;
}
</style>
